import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Typography, useTheme } from '@mui/material'
import { useFormikContext } from 'formik'

import {
  EScanToEarnProgressions,
  useScanToEarnContext,
} from '../form-wrapper/scan-to-earn-context.component'
import { IScanToEarnCreateForm } from 'src/types/scan-to-earn.type'
import { HelperWrapper } from 'src/components/helper-wrapper'
import { MediaInput } from 'src/components/media-input'
import { useFindImpacts, useGetScanInformation, useGetScanToEarnReward } from 'src/hooks'
import { Loader, LoaderWithText } from 'src/components/loader'

import { FormSubmitVerifyModal } from '../form-wrapper/form-submit-verify-modal.component'
import { TextInputWithHelper } from 'src/components/text-input-with-helper-icon'
import { ETranslationNs } from 'src/types'
import { MediaValidationModal } from '../media-validation-modal.component'

import { useStyles } from '../styles'
import ImpactList from '../impacts-list.component'

export const DetailsProgression = () => {
  const [verifyOpen, setVerifyOpen] = useState(false)
  const [invalidMediaMessage, setInvalidMediaMessage] = useState<null | string>(null)
  const [tempMediaUrls, setTempMediaUrls] = useState<null | string[]>(null)
  const [hasTriedImpacts, setHasTriedImpacts] = useState(false)

  const [t] = useTranslation()
  const theme = useTheme()
  const classes = useStyles()
  const { progression, setProgression } = useScanToEarnContext()
  const {
    setValues,
    setFieldValue,
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    isSubmitting,
    submitForm,
  } = useFormikContext<IScanToEarnCreateForm>()

  const { mutateAsync, isLoading } = useGetScanInformation()
  const { mutateAsync: findImpacts, isLoading: isFindImpactsLoading } = useFindImpacts()

  const { data, isLoading: isScanToEarnPriceLoading } = useGetScanToEarnReward()

  useEffect(() => {
    const fetchData = async () => {
      if (progression === EScanToEarnProgressions.DETAILS) {
        try {
          const response = await mutateAsync({
            barcode_id: values.barcode_value,
            mediaFiles: values.media
              .map((item) => item.file)
              .filter((file): file is File => file !== null),
          })

          let changedValues: any = {}

          if (response) {
            if (!response.media_validation) {
              setInvalidMediaMessage(response.media_validation_message)
            }

            if (response?.summary) {
              changedValues.summary = response.summary
            }
            if (response?.company_name) {
              changedValues.company_name = response.company_name
              // setFieldValue('company_name', response.company_name)
            }
            if (response?.product_name) {
              changedValues.product_name = response.product_name
              // setFieldValue('product_name', response.product_name)
            }
            if (response?.impacts && response.impacts.length > 0) {
              changedValues.impacts = response.impacts
              // setFieldValue('impacts', response.impacts)
            }
            if (response.new_impacts && response.new_impacts.length > 0) {
              changedValues.new_impacts = response.new_impacts
              // setFieldValue('new_impacts', response.new_impacts)
            }

            setValues((values) => ({ ...values, ...changedValues }))

            if (response?.tempMediaUrls && response.tempMediaUrls.length > 0) {
              setTempMediaUrls(response.tempMediaUrls)
            }
          }
        } catch (error) {
          console.error('Failed to fetch data:', error)
        }
      }
    }

    fetchData()
  }, [progression])

  const deleteImpactHandler = (id: string) => {
    const updatedImpacts = values.impacts.filter((impact) => impact.id !== id)

    setFieldValue('impacts', updatedImpacts)
  }

  const deleteNewImpactHandler = (name: string) => {
    const updatedImpacts = values.new_impacts.filter((impact) => impact !== name)

    setFieldValue('new_impacts', updatedImpacts)
  }

  const handleBackClick = () => {
    setValues((values) => ({
      ...values,
      summary: '',
      company_name: '',
      product_name: '',
      impacts: [],
      new_impacts: [],
    }))
    setHasTriedImpacts(false)
    setProgression(EScanToEarnProgressions.ADD_MEDIA)
    setInvalidMediaMessage(null)
  }

  const handleCloseMediaValidationModal = () => {
    setProgression(EScanToEarnProgressions.ADD_MEDIA)
    setInvalidMediaMessage(null)
  }

  const handleContinueClick = () => {
    setVerifyOpen(true)
  }

  const handleSubmit = async () => {
    await submitForm()
    setVerifyOpen(false)
  }

  const handleVerifyClose = () => {
    setVerifyOpen(false)
  }

  const addNewImpactHandler = (name: string) => {
    setFieldValue('new_impacts', [...values.new_impacts, name])
  }

  const handleTryToFindImpactsOneMoreTime = async () => {
    if (hasTriedImpacts) return
    setHasTriedImpacts(true)

    if (tempMediaUrls && tempMediaUrls.length > 0) {
      const response = await findImpacts({ tempMediaUrls })

      if (response) {
        let changedValues: any = {}

        if (response?.impacts && response.impacts.length > 0) {
          changedValues.impacts = response.impacts
        }
        if (response.new_impacts && response.new_impacts.length > 0) {
          changedValues.new_impacts = response.new_impacts
        }

        setValues((values) => ({ ...values, ...changedValues }))
      }
    }
  }

  if (progression !== EScanToEarnProgressions.DETAILS) {
    return null
  }

  if (progression === EScanToEarnProgressions.DETAILS && isLoading) {
    return (
      <LoaderWithText
        text={
          <Typography fontSize={'14px'} fontWeight="bold">
            Traicy Analyzing
          </Typography>
        }
      />
    )
  }

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '40px',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px',
            width: '100%',
          }}
        >
          <Typography sx={{ fontSize: '24px', fontWeight: 700 }}>
            <HelperWrapper t="scan-to-earn-details-step-header-text" />
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              maxWidth: '600px',
            }}
          >
            <TextInputWithHelper
              inputName="barcode_value"
              handleChange={handleChange}
              handleBlur={handleBlur}
              value={values.barcode_value}
              inputUpperLabel={
                <HelperWrapper
                  t={'scan-to-earn-barcode-input-label'}
                  ns={ETranslationNs.Common}
                  isInputLable
                />
              }
              inputPlaceholder={t('scan-to-earn-barcode-input-placeholder.v', {
                ns: ETranslationNs.Common,
              })}
              fullWidth
              disabled
            />

            <TextInputWithHelper
              inputName="company_name"
              handleChange={handleChange}
              handleBlur={handleBlur}
              value={values.company_name}
              inputUpperLabel={
                <HelperWrapper
                  t={'scan-to-earn-company-name-input-label'}
                  ns={ETranslationNs.Common}
                  isInputLable
                  isMandatory
                />
              }
              inputPlaceholder={t('scan-to-earn-company-name-input-placeholder.v', {
                ns: ETranslationNs.Common,
              })}
              error={
                touched.company_name && errors.company_name ? (
                  <HelperWrapper t={errors.company_name} />
                ) : null
              }
              fullWidth
              withoutPlaceholderPrepend
            />

            <TextInputWithHelper
              inputName="product_name"
              handleChange={handleChange}
              handleBlur={handleBlur}
              value={values.product_name}
              inputUpperLabel={
                <HelperWrapper
                  t={'scan-to-earn-product-name-input-label'}
                  ns={ETranslationNs.Common}
                  isInputLable
                  isMandatory
                />
              }
              inputPlaceholder={t('scan-to-earn-product-name-input-placeholder.v', {
                ns: ETranslationNs.Common,
              })}
              error={
                touched.product_name && errors.product_name ? (
                  <HelperWrapper t={errors.product_name} />
                ) : null
              }
              fullWidth
              withoutPlaceholderPrepend
            />
          </Box>

          <Box sx={{ width: '100%', maxWidth: '600px' }}>
            <MediaInput
              disabled
              mainImageSwitch
              inputName="media"
              media={values.media}
              setFieldValue={setFieldValue}
              scanToEarnMode
            />
          </Box>

          <ImpactList
            impacts={values.impacts}
            newImpacts={values.new_impacts}
            onDeleteImpact={deleteImpactHandler}
            onDeleteNewImpact={deleteNewImpactHandler}
            onAddNewImpact={addNewImpactHandler}
            hasTriedImpacts={hasTriedImpacts}
            isFindImpactsLoading={isFindImpactsLoading}
            handleTryToFindImpactsOneMoreTime={handleTryToFindImpactsOneMoreTime}
          />

          <Box sx={{ width: '100%', maxWidth: '600px' }}>
            <TextInputWithHelper
              inputName="summary"
              // disabled
              handleChange={handleChange}
              handleBlur={handleBlur}
              value={values.summary}
              error={touched.summary && errors.summary ? t(errors.summary + '.v') : null}
              inputPlaceholder={t('scan-to-earn-product-description-input-placeholder.v')}
              inputUpperLabel={
                <HelperWrapper
                  t="scan-to-earn-product-description-input-label"
                  isMandatory
                  isColorInherited
                  verticalPosition={15}
                  size={12}
                />
              }
              multiline={true}
            />
          </Box>
        </Box>

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '30px',
            mb: '40px',
          }}
        >
          <Button
            disabled={isFindImpactsLoading}
            onClick={handleBackClick}
            variant="outlined"
            size="small"
          >
            <HelperWrapper t="scan-to-earn-back-button-label" isColorInherited />
          </Button>
          <Button
            type="submit"
            disabled={
              !!Object.keys(errors).length ||
              isScanToEarnPriceLoading ||
              isLoading ||
              !values.company_name ||
              !values.product_name ||
              !values.summary ||
              isFindImpactsLoading
            }
            variant="contained"
            size="small"
            onClick={handleContinueClick}
          >
            <HelperWrapper t="scan-to-earn-submit-button-label" isColorInherited />
          </Button>
        </Box>
      </Box>
      <FormSubmitVerifyModal
        isProceeding={isSubmitting}
        handleProceed={handleSubmit}
        open={verifyOpen}
        handleClose={handleVerifyClose}
        barcodeId={values.barcode_value}
        priceAmount={data?.amount!}
      />
      <MediaValidationModal
        handleClose={handleCloseMediaValidationModal}
        open={!!invalidMediaMessage}
        traicyRejectReason={invalidMediaMessage!}
      />
    </>
  )
}
